import { toastFailure } from '@/lib/api';
import { Bookmark, BookmarkPlus, X } from 'lucide-react';
import * as React from 'react';

import {
  Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, Input,
} from '@/components/cargofl-ui';
import {
  fromQuery, toQuery, useDeleteFilter, useSaveFilter, useSavedFilters, type Filters,
} from '@/lib/reporting-api';

/**
 * Save a set of filters and get it back later.
 *
 * Stored as the query string the screens already build, so this component never
 * has to know what a filter contains - adding a filter field costs nothing here.
 *
 * Per screen, because the same filters mean different things on All times and on
 * Export, and offering one on the other is noise.
 */
export function SavedFilters({
  screen,
  value,
  onChange,
}: {
  screen: string;
  value: Filters;
  onChange: (next: Filters) => void;
}) {
  const { data: filters } = useSavedFilters(screen);
  const save = useSaveFilter(screen);
  const remove = useDeleteFilter(screen);
  const [naming, setNaming] = React.useState(false);
  const [name, setName] = React.useState('');

  const current = toQuery(value);
  const active = filters?.find((f) => f.query === current);

  const commit = async () => {
    if (!name.trim()) return;
    try {
      await save.mutateAsync({ name: name.trim(), query: current });
    } catch (err) {
      return toastFailure(`Could not save "${name.trim()}"`)(err);
    }
    setNaming(false);
    setName('');
  };

  if (naming) {
    return (
      <span className="flex items-center gap-1.5">
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void commit();
            if (e.key === 'Escape') setNaming(false);
          }}
          placeholder="Name this filter"
          className="h-9 w-48"
        />
        <Button size="sm" onClick={() => void commit()} disabled={!name.trim() || save.isPending}>
          Save
        </Button>
        <Button size="sm" variant="ghost" aria-label="Don't save" hint="Don't save this filter" onClick={() => setNaming(false)}>
          <X className="size-4" />
        </Button>
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button hint={"Open a saved filter, or save the current one"} variant="outline" size="sm">
          <Bookmark className="size-4" />
          {active ? active.name : 'Saved filters'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Saved filters</DropdownMenuLabel>
        {(filters ?? []).length === 0 && (
          <DropdownMenuItem disabled>None saved yet</DropdownMenuItem>
        )}
        {(filters ?? []).map((f) => (
          <DropdownMenuItem
            key={f.id}
            onSelect={() => onChange(fromQuery(f.query))}
            className="flex items-center justify-between gap-2"
          >
            <span className="truncate">{f.name}</span>
            {/* Delete sits inside the row rather than behind a second menu: it is
                the only other thing anybody does to a saved filter. */}
            <span
              role="button"
              tabIndex={0}
              aria-label={`Delete ${f.name}`}
              className="shrink-0 rounded p-0.5 opacity-50 hover:bg-muted hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); remove.mutate(f.id, { onError: toastFailure(`Could not delete "${f.name}"`) }); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  remove.mutate(f.id, { onError: toastFailure(`Could not delete "${f.name}"`) });
                }
              }}
            >
              <X className="size-3.5" />
            </span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => { setName(''); setNaming(true); }}>
          <BookmarkPlus className="size-4" />
          {active ? 'Save as a new filter' : 'Save these filters'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
